import { useMemo, useState } from "react";
import Icon from "~/components/shared/Icon";
import cn from "classnames";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "~/components/shared/PopoverNext";

export type PipelineTemplate = {
  uuid?: string;
  name: string;
  description?: string | null;
  configuration: string;
  type: "custom" | "private" | "public";
  editable: boolean;
};

export type PipelineTemplateSelectProps = {
  className?: string;
  selected?: PipelineTemplate | null;
  templates: PipelineTemplate[];
  orgTemplatesRequired?: boolean;
  disabled?: boolean;
  onTemplateSelect: (template: PipelineTemplate | null) => void;
};

export const NONE_TEMPLATE: PipelineTemplate = {
  name: "None",
  description: "",
  configuration: "",
  editable: true,
  type: "custom",
};

export function PipelineTemplateSelect({
  className,
  selected,
  templates,
  orgTemplatesRequired = false,
  disabled = false,
  onTemplateSelect,
}: PipelineTemplateSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedTemplate = selected || NONE_TEMPLATE;

  const publicTemplates = useMemo(
    () => templates.filter((template) => template.type === "public"),
    [templates],
  );
  const privateTemplates = useMemo(
    () => templates.filter((template) => template.type === "private"),
    [templates],
  );

  // When templates are required, don't show "None" or custom templates
  const showCustomTemplates = !orgTemplatesRequired;
  const showNoneOption = !orgTemplatesRequired;

  const handleTemplateClick = (template: PipelineTemplate) => {
    onTemplateSelect(template.type === "custom" ? null : template);
    setIsOpen(false);
  };

  return (
    <>
      <Popover open={disabled ? false : isOpen} onOpenChange={disabled ? undefined : setIsOpen}>
        <PopoverTrigger title={selectedTemplate?.name} asChild={true} disabled={disabled}>
          <button
            disabled={disabled}
            data-testid="pipeline-templates-dropdown"
            className={cn([
              "btn btn-small btn-depth btn-flex w-fit min-w-24 max-w-[300px] items-center gap-1 px-2",
              disabled && "cursor-not-allowed opacity-50",
              className,
            ])}
          >
            <span className="dark-gray">Template:</span>
            <span className="truncate text-black">{selectedTemplate?.name}</span>
            {!disabled && <Icon icon="down-triangle" className="h-2 w-2" />}
          </button>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            alignOffset={0}
            avoidCollisions={false}
            align="end"
            arrow={false}
            data-testid="pipeline-templates-dropdown-content"
            className="mt-1 max-h-96 w-80 overflow-y-auto p-0"
          >
            {showNoneOption && (
              <div className="p-1">
                <TemplateButton
                  onClick={() => handleTemplateClick(NONE_TEMPLATE)}
                  template={NONE_TEMPLATE}
                  selected={selected === null}
                />
              </div>
            )}

            {showCustomTemplates && (
              <>
                <div className="bg-silver border-b border-t border-gray-300 px-2 py-1 text-xs font-medium text-black first:border-t-0">
                  Helper templates
                </div>
                <div className="flex flex-col gap-px p-1">
                  {publicTemplates.map((template) => (
                    <TemplateButton
                      key={template.name}
                      onClick={() => handleTemplateClick(template)}
                      template={template}
                      selected={template.uuid === selected?.uuid}
                    />
                  ))}
                </div>
              </>
            )}

            {privateTemplates.length > 0 && (
              <>
                <div className="bg-silver border-b border-t border-gray-300 px-2 py-1 text-xs font-medium text-black first:border-t-0">
                  Organization templates
                </div>
                <div className="flex flex-col gap-px p-1">
                  {privateTemplates.map((template) => (
                    <TemplateButton
                      key={template.uuid || template.name}
                      onClick={() => handleTemplateClick(template)}
                      template={template}
                      selected={template.uuid === selected?.uuid}
                    />
                  ))}
                </div>
              </>
            )}
          </PopoverContent>
        </PopoverPortal>
      </Popover>

      <input
        type="hidden"
        name={
          selectedTemplate?.type === "private"
            ? "project[pipeline_template_uuid]"
            : "project[pipeline_public_template_id]"
        }
        value={selectedTemplate?.uuid || ""}
      />
    </>
  );
}

const TemplateButton = ({
  template,
  onClick,
  selected,
}: {
  template: PipelineTemplate;
  onClick?: () => void;
  selected: boolean;
}) => {
  return (
    <button
      className={cn([
        "flex w-full cursor-pointer flex-col justify-center gap-0 rounded px-2 py-1 text-black hover:bg-gray-200",
        selected && "!bg-purple-100 !text-purple-600 hover:ring-1 hover:ring-purple-300",
      ])}
      onClick={onClick}
    >
      <span className="text-left">{template.name}</span>
      {template.description && (
        <span
          className={cn(["dark-gray text-left text-xs", selected && "!text-purple-600 opacity-60"])}
        >
          {template.description}
        </span>
      )}
    </button>
  );
};
